import { Link } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { Rocket } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/providers/auth'
import { usePermission } from '@/providers/permissions'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { companyConfigQueryKey, updateCompanyConfig } from '@/lib/companies'
import { errorMessage } from '@/lib/errors'
import { useCompanyConfig } from '@/hooks/useCompanyConfig'
import { ONBOARDING_PATH, ONBOARDING_STEPS, completedOnboardingSteps, isOnboardingOpen, type OnboardingState } from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import type { CompanyConfig } from '@/pages/Companies/GetCompany/types/config'

export function OnboardingCard({ collapsed, onNavigate }: { collapsed?: boolean; onNavigate?: () => void }) {
  const { company } = useAuth()
  const { hasPermission } = usePermission()
  const queryClient = useQueryClient()
  const canWrite = hasPermission('settings', 'write')
  const queryKey = companyConfigQueryKey(company.id)
  const { data } = useCompanyConfig()

  const state = data?.onboarding
  if (!canWrite || !isOnboardingOpen(state)) return null

  const completed = completedOnboardingSteps(state)
  const label = `${completed.length} de ${ONBOARDING_STEPS.length} passos concluídos`

  const dismiss = async () => {
    const next: OnboardingState = { ...state, status: 'dismissed' }
    queryClient.setQueryData<CompanyConfig | undefined>(queryKey, current => (current ? { ...current, onboarding: next } : current))
    try {
      await updateCompanyConfig({ onboarding: next }, company.id)
    } catch (error) {
      queryClient.invalidateQueries(queryKey)
      toast.error(errorMessage(error, 'Não foi possível dispensar a configuração inicial.'))
    }
  }

  if (collapsed) {
    return (
      <div className="flex justify-center p-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" asChild>
              <Link to={ONBOARDING_PATH} onClick={onNavigate} aria-label={`Continuar configuração inicial: ${label}`}>
                <Rocket />
              </Link>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs">
            Configuração inicial · {label}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  return (
    <div className="p-3">
      <div className="rounded-xl border border-sidebar-border bg-card p-3" data-testid="onboarding-card">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
            <Rocket className="h-4 w-4" />
          </span>
          <div className="min-w-0 leading-tight">
            <p className="text-sm font-semibold">Configuração inicial</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        </div>
        <div className="mt-3 flex gap-1" aria-hidden>
          {ONBOARDING_STEPS.map(step => (
            <span key={step} className={cn('h-1.5 flex-1 rounded-full', completed.includes(step) ? 'bg-primary' : 'bg-secondary')} />
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" asChild className="flex-1">
            <Link to={ONBOARDING_PATH} onClick={onNavigate}>
              Continuar
            </Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={dismiss}>
            Dispensar
          </Button>
        </div>
      </div>
    </div>
  )
}
